import { useCallback, useMemo, useState, type ReactElement } from 'react'
import { isEmpty, isNotEmpty, type TaskData } from '@terros/common'
import {
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type RowSelectionState,
  type SortingState,
} from '@tanstack/react-table'
import { createFileRoute } from '@tanstack/react-router'
import { modals } from '@mantine/modals'
import { Group, Text, TextInput } from '@mantine/core'
import { useDebounce } from '@hooks'
import { usePreference, useTaskManagement, useTranslation } from '@front/model'
import { ChooseDates, Page, TTable, useRangeQueryState } from '@components'
import { QuizPageContainer } from '../quiz_.$quizId/QuizPageContainer'
import { useQuizContext } from '../quiz_.$quizId/context'
import { useAssigneeTableColumns } from './useColumns'
import { ToggleAssigneeColumns } from './ToggleAssigneeColumns'
import { useAssigneeTanStackColumns } from './tanstackColumns'
import { getAvailableColumns } from './columns'
import { AssignUsers } from './AssignUsers'
import { AssigneesBulkActions } from './AssigneesBulkActions'

function QuizAssigneesRoute(): ReactElement {
  const { quiz } = useQuizContext()
  const { t } = useTranslation()

  const [selectedTasks, setSelectedTasks] = useState<TaskData[]>([])
  const [sorting, setSorting] = useState<SortingState>([])
  const [query, setQuery] = useState('')
  const debouncedQuery = useDebounce(query, 300)

  const { range, setRange } = useRangeQueryState('quiz-tasks', { type: 'allTime' })
  const { data: taskLockout } = usePreference('taskLockout')
  const { selectedColumns, setSelectedColumns } = useAssigneeTableColumns()

  const { data, isLoading, error, isFetching, refetch } = useTaskManagement({
    quizIds: [quiz.quizId],
    startDate: range[0]?.getTime(),
    endDate: range[1]?.getTime(),
    query: debouncedQuery || undefined,
  })

  const rows = useMemo(() => data ?? [], [data])
  const clearSelection = useCallback(() => setSelectedTasks([]), [])

  const effectiveColumns = useMemo(() => {
    const availableIds = new Set(getAvailableColumns(taskLockout).map((c) => c.id))
    return selectedColumns.filter((id) => availableIds.has(id))
  }, [selectedColumns, taskLockout])

  const columns = useAssigneeTanStackColumns({ selectedColumnIds: effectiveColumns })

  const rowSelection: RowSelectionState = useMemo(
    () => Object.fromEntries(selectedTasks.map((task) => [task.taskId, true])),
    [selectedTasks]
  )

  const handleRowSelectionChange = useCallback(
    (updaterOrValue: RowSelectionState | ((prev: RowSelectionState) => RowSelectionState)) => {
      const newSelection = typeof updaterOrValue === 'function' ? updaterOrValue(rowSelection) : updaterOrValue
      const selectedIds = new Set(Object.keys(newSelection).filter((k) => newSelection[k]))
      setSelectedTasks(rows.filter((r) => selectedIds.has(r.taskId)))
    },
    [rowSelection, rows]
  )

  const handleQueryChange = (value: string): void => {
    if (isEmpty(selectedTasks)) {
      setQuery(value)
      return
    }
    modals.openConfirmModal({
      title: t('Clear selection?'),
      children: (
        <Text size='sm'>
          {t('Searching will clear your {{count}} selected assignees.', { count: selectedTasks.length })}
        </Text>
      ),
      labels: { cancel: t('Cancel'), confirm: t('Continue') },
      onConfirm: () => {
        clearSelection()
        setQuery(value)
      },
    })
  }

  const handleRangeChange: typeof setRange = (newRange) => {
    if (isEmpty(selectedTasks)) {
      setRange(newRange)
      return
    }
    modals.openConfirmModal({
      title: t('Clear selection?'),
      children: (
        <Text size='sm'>
          {t('Changing the date range will clear your {{count}} selected assignees.', { count: selectedTasks.length })}
        </Text>
      ),
      labels: { cancel: t('Cancel'), confirm: t('Continue') },
      onConfirm: () => {
        clearSelection()
        setRange(newRange)
      },
    })
  }

  const table = useReactTable({
    data: rows,
    columns,
    state: { rowSelection, sorting },
    onRowSelectionChange: handleRowSelectionChange,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableMultiSort: false,
    enableRowSelection: true,
    getRowId: (row) => row.taskId,
  })

  return (
    <QuizPageContainer>
      <Page.Heading isFetching={isFetching} refetch={refetch} title={t('Assignees')}>
        <AssignUsers />
      </Page.Heading>
      <Page.FunctionBar>
        <Group gap='sm'>
          <TextInput
            placeholder={t('Search')}
            value={query}
            onChange={(e) => handleQueryChange(e.currentTarget.value)}
          />
          <ChooseDates range={range} setRange={handleRangeChange} />
          <ToggleAssigneeColumns
            selectedColumns={selectedColumns}
            setSelectedColumns={setSelectedColumns}
            taskLockout={taskLockout}
          />
        </Group>
      </Page.FunctionBar>
      <Page.Content>
        <TTable table={table} data={rows} isLoading={isLoading} error={error} emptyTitle={t('No assignees')} sortable />
        {isNotEmpty(selectedTasks) && (
          <AssigneesBulkActions selectedTasks={selectedTasks} clearSelection={clearSelection} />
        )}
      </Page.Content>
    </QuizPageContainer>
  )
}

export const Route = createFileRoute('/quiz_/$quizId/assignees/')({
  component: QuizAssigneesRoute,
})
